import { useCallback, useEffect, useRef, useState } from "react";
import {
  computeFactorPeriodReturn,
  computeCumulativePerformance,
  resolveLaunchClipMs,
} from "../components/performanceChartV3Utils";
import type {
  StrategyEntry,
  StrategyUpdateEntry,
} from "../services/investorStrategyService";
import {
  discoverFactorsService,
  type DiscoverFactorProductType,
  type DiscoverFactorsBatchRequest,
} from "../services/discoverFactorsService";
import { computeProductAddedDates } from "../utils/strategyAddedDates";
import type {
  ProductLookupEntry,
  ProductLookupKey,
} from "../utils/strategyUtils";
import type { StrategyIdProductMap } from "./useStrategyData";

const SINCE_ADDED_PAGE_SIZE = 10000;

export interface StrategySinceAddedValue {
  addedAtUtc: string | null;
  returnPct: number | null;
  todayReturnPct?: number | null;
}

export type StrategySinceAddedByProduct = Partial<
  Record<ProductLookupKey, StrategySinceAddedValue>
>;

export interface UseStrategySinceAddedReturnsParams {
  enabled: boolean;
  strategies: StrategyEntry[];
  allHistoryItems: StrategyUpdateEntry[];
  strategyIdProductMap: StrategyIdProductMap;
  productLookup: Map<ProductLookupKey, ProductLookupEntry>;
  isLoadingStrategies: boolean;
  isLoadingHistory: boolean;
  launchDate?: string | null;
}

export interface UseStrategySinceAddedReturnsResult {
  sinceAddedByProduct: StrategySinceAddedByProduct;
  isLoadingSinceAdded: boolean;
  error: string | null;
  retry: () => void;
}

function getProductKey(entry: Pick<StrategyEntry, "entityType" | "productId">): ProductLookupKey {
  return `${entry.entityType}-${entry.productId}`;
}

function computeReturnPct(factors: Array<{ t: string; f: number }>): number | null {
  if (factors.length === 0) return null;
  return computeCumulativePerformance(
    factors.map((factor) => ({
      metricDate: factor.t,
      metricValue: factor.f,
    }))
  );
}

function computeTodayReturnPct(
  factors: Array<{ t: string; f: number }>,
  addedAtUtc: string | null
): number | null {
  return computeFactorPeriodReturn(factors, "Today", addedAtUtc);
}

function resolveSinceAddedStartUtc(
  addedAtUtc: string | null,
  launchMs: number | null
): string | null {
  if (!addedAtUtc || launchMs === null) return addedAtUtc;

  const addedAtMs = new Date(addedAtUtc).getTime();
  if (Number.isNaN(addedAtMs) || addedAtMs >= launchMs) return addedAtUtc;

  return new Date(launchMs).toISOString();
}

export function useStrategySinceAddedReturns({
  enabled,
  strategies,
  allHistoryItems,
  strategyIdProductMap,
  productLookup,
  isLoadingStrategies,
  isLoadingHistory,
  launchDate,
}: UseStrategySinceAddedReturnsParams): UseStrategySinceAddedReturnsResult {
  const [sinceAddedByProduct, setSinceAddedByProduct] =
    useState<StrategySinceAddedByProduct>({});
  const [isLoadingSinceAdded, setIsLoadingSinceAdded] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [retryAttempt, setRetryAttempt] = useState(0);
  const fetchIdRef = useRef(0);

  useEffect(() => {
    const fetchId = ++fetchIdRef.current;

    if (!enabled || isLoadingStrategies || isLoadingHistory || strategies.length === 0) {
      setSinceAddedByProduct({});
      setIsLoadingSinceAdded(false);
      setError(null);
      return;
    }

    const addedDates = computeProductAddedDates(
      strategies,
      allHistoryItems,
      strategyIdProductMap
    );
    const launchMs = resolveLaunchClipMs(launchDate);
    const base: StrategySinceAddedByProduct = {};
    const requests: DiscoverFactorsBatchRequest[] = [];

    for (const strategy of strategies) {
      const key = getProductKey(strategy);
      const addedAtUtc = resolveSinceAddedStartUtc(addedDates.get(key) ?? null, launchMs);
      base[key] = { addedAtUtc, returnPct: null, todayReturnPct: null };

      const lookup = productLookup.get(key);
      if (!addedAtUtc || !lookup) continue;

      requests.push({
        providerId: lookup.providerId,
        productType: strategy.entityType.toLowerCase() as DiscoverFactorProductType,
        productId: strategy.productId,
        from: addedAtUtc,
        pageSize: SINCE_ADDED_PAGE_SIZE,
      });
    }

    setSinceAddedByProduct(base);
    setError(null);

    if (requests.length === 0) {
      setIsLoadingSinceAdded(false);
      return;
    }

    let cancelled = false;
    setIsLoadingSinceAdded(true);

    discoverFactorsService
      .getFactorsBatch(requests)
      .then((results) => {
        if (cancelled || fetchId !== fetchIdRef.current) return;

        const next: StrategySinceAddedByProduct = { ...base };
        for (const result of results) {
          if (result.failed) continue;
          const key = `${result.productType.toUpperCase()}-${result.productId}` as ProductLookupKey;
          next[key] = {
            addedAtUtc: next[key]?.addedAtUtc ?? result.from ?? null,
            returnPct: computeReturnPct(result.factors),
            todayReturnPct: computeTodayReturnPct(
              result.factors,
              next[key]?.addedAtUtc ?? result.from ?? null
            ),
          };
        }
        setSinceAddedByProduct(next);
        if (results.some((result) => result.failed)) {
          setError("Some model returns could not be loaded.");
        }
      })
      .catch(() => {
        if (!cancelled && fetchId === fetchIdRef.current) {
          setSinceAddedByProduct(base);
          setError("Failed to load model returns.");
        }
      })
      .finally(() => {
        if (!cancelled && fetchId === fetchIdRef.current) {
          setIsLoadingSinceAdded(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [
    enabled,
    strategies,
    allHistoryItems,
    strategyIdProductMap,
    productLookup,
    isLoadingStrategies,
    isLoadingHistory,
    launchDate,
    retryAttempt,
  ]);

  const retry = useCallback(() => {
    setRetryAttempt((current) => current + 1);
  }, []);

  return { sinceAddedByProduct, isLoadingSinceAdded, error, retry };
}
